/* eslint-disable react/jsx-no-target-blank */
import React from 'react';
import { Skeleton } from '@dsg-tech/homefield-react';
import { StoreIdentifierName } from 'types';
import { useAppSelector } from 'store/store';
import { getFeatureFlagByNameSelector } from 'store/selectors';
import ErrorBoundaryWithLogger from 'components/common/ErrorBoundary/ErrorBoundary';
import useFeatureConfiguration from 'hooks/useFeatureConfiguration';
import useLogger from 'hooks/useLogger';
import { checkStores } from '../../../utility/utility';
import { TickerData, TickerModification, TickerResponse } from './Ticker.interface';
import useHomr from '../../../hooks/useHomr';
import ExpandableTicker from './ExpandableTicker/ExpandableTicker';
import CarouselTicker from './CarouselTicker/CarouselTicker';
import './Ticker.scss';
import { FEATURE_CONFIGURATION_KEYS } from '../../../utility/constants';
import { applyModification } from './Ticker.utils';

export const TickerFallback = () => (
  <div className="header-ticker-minimum">
    <Skeleton className="header-ticker-minimum" />
  </div>
);

type Props = {
  storeIdentifierName: StoreIdentifierName;
};

const Ticker = ({ storeIdentifierName }: Props) => {
  const tickerHomrData = useHomr('ticker', 'default', storeIdentifierName) as
    | TickerResponse[]
    | null;
  const tickerApiResponse: TickerResponse | null = tickerHomrData ? tickerHomrData[0] : null;
  const expandableTickerFlag = useAppSelector(getFeatureFlagByNameSelector('expandableTicker'));
  const config = useFeatureConfiguration(FEATURE_CONFIGURATION_KEYS.MODIFY_TICKER);
  const modifications: TickerModification[] =
    (config?.params?.modifications as TickerModification[]) || [];

  const logger = useLogger();

  const createTickerLink = (linkText: string): string => {
    if (typeof linkText !== 'string') return '';
    if (
      linkText.startsWith('/') ||
      linkText.startsWith('http://') ||
      linkText.startsWith('https://')
    ) {
      return linkText;
    }
    return `/${linkText}`;
  };

  const getTickerMessages = (tickerData: TickerData[], homeRTickerData: TickerResponse) => {
    return tickerData.map((ticker, idx: number) => {
      const dataEmTemplateTag = homeRTickerData.templateTag?.replace(/\s/g, '') || '';
      const dataEmLinkTitle = ticker.linkTitle?.replace(/\s/g, '') || '';
      const dataEmTopText = ticker.topText?.replace(/\s/g, '') || '';

      if (ticker.topText !== '') {
        return (
          // eslint-disable-next-line react/no-array-index-key
          <div key={idx} className="app-ticker-message">
            <div
              className="ticker__section hmf-position-relative hmf-p-xxs"
              style={{ borderColor: tickerData[idx].foregroundColor }}>
              <a
                className="hmf-display-flex hmf-flex-row hmf-justify-content-center hmf-text-decoration-none"
                href={checkStores(createTickerLink(ticker.link))}
                target={
                  checkStores(createTickerLink(ticker.link))?.includes('http') ? '_blank' : '_self'
                }
                data-em={
                  tickerData[idx].dataEm ??
                  `tkr_${dataEmTemplateTag}_${idx + 1}_ticker-${
                    idx + 1
                  }_na_na${dataEmLinkTitle}_${dataEmTopText}`
                }>
                <span
                  className="ticker__message ticker__message--main ticker__message--bold hmf-body-bold-s hmf-text-align-center hmf-display-inline-block hmf-mb-0 hmf-align-self-baseline"
                  style={{ color: tickerData[idx].foregroundColor }}>
                  {ticker.topText}
                </span>
                <span
                  className="ticker__message ticker__message--disclaimer hmf-legal-s hmf-align-self-baseline"
                  style={{ color: tickerData[idx].foregroundColor }}>
                  {ticker.bottomText}{' '}
                  <span className="ticker__message--underline hmf-legal-s hmf-text-decoration-underline">
                    {ticker.linkTitle}
                  </span>
                </span>
              </a>
            </div>
          </div>
        );
      }
      return '';
    });
  };

  const personalizeTickerData = (originalData: TickerData[]): TickerData[] => {
    try {
      if (!originalData || originalData.length === 0) return [];
      const defaultBackgroundColor = originalData[0].backgroundColor ?? 'black';
      const defaultForegroundColor = originalData[0].foregroundColor ?? 'white';

      return modifications.reduce(
        (data, modification) =>
          applyModification(
            data,
            modification,
            defaultBackgroundColor,
            defaultForegroundColor,
            logger,
          ),
        [...originalData],
      );
    } catch (error) {
      logger.error('Error updating personalized ticker data:', String(error));
      return originalData ?? [];
    }
  };

  const generateTickerData = () => {
    if (!tickerApiResponse) return null;
    const { content, templateName } = tickerApiResponse;
    if (!content || Object.keys(content).length === 0) return null;

    let tickerContentData: TickerData[] = [];
    let tickerLink = '';

    if (
      templateName === 'ticker' ||
      templateName === 'split_ticker' ||
      templateName === 'carousel_ticker'
    ) {
      const splitTicker = content?.['Split Ticker']?.[0];

      if (splitTicker) {
        tickerContentData = splitTicker['Ticker Content'] || [];
        tickerContentData = tickerContentData.map((tickerSlide) => ({
          ...tickerSlide,
          foregroundColor: splitTicker.foregroundColor ?? '',
          backgroundColor: splitTicker.backgroundColor ?? '',
        }));

        if (modifications.length > 0) {
          tickerContentData = personalizeTickerData(tickerContentData);
        }
        tickerLink = createTickerLink(splitTicker['Ticker Content']?.[0]?.link || '');
      }
    }

    // Replace all tickers with ExpandableTicker when flag is enabled
    if (expandableTickerFlag) {
      return (
        <ErrorBoundaryWithLogger componentId="Ticker-ExpandableTicker">
          <ExpandableTicker tickerData={tickerContentData} />
        </ErrorBoundaryWithLogger>
      );
    }

    // Render CarouselTicker if carousel_ticker template is explicitly set
    if (templateName === 'carousel_ticker') {
      return (
        <ErrorBoundaryWithLogger componentId="Ticker-CarouselTicker">
          <CarouselTicker tickerData={tickerContentData} />
        </ErrorBoundaryWithLogger>
      );
    }

    // Default rendering for other tickers when flag is not enabled
    const classes = [
      'rh-ticker-container header-ticker-minimum ticker-dsg hmf-align-items-center hmf-display-flex hmf-flex-row hmf-justify-content-center hmf-position-relative hmf-text-align-center hmf-text-decoration-none',
      tickerContentData.length > 1 ? 'ticker-dsg--multi' : 'ticker-dsg--single',
    ].join(' ');

    return (
      <a
        href={tickerLink}
        className={classes}
        style={{ backgroundColor: tickerContentData[0]?.backgroundColor ?? 'black' }}>
        {getTickerMessages(tickerContentData, tickerApiResponse)}
      </a>
    );
  };

  return <>{!tickerApiResponse ? <TickerFallback /> : generateTickerData()}</>;
};

export default Ticker;
